import React, { useState } from "react";
import classNames from "classnames";
import { Link, graphql, useStaticQuery } from "gatsby";
import Img from "gatsby-image";

type DraftboxSettings = {
  draftboxSettings: {
    content: {
      header: {
        navigation: Array<{ label: string; url: string }>;
        logoType: "firstLetter" | "text";
        logoText: string;
        logoLink: string;
        openLogoLinkInNewTab: boolean;
      };
    };
    identity: {
      logoUrl: {
        publicURL: string;
        childImageSharp: {
          fixed: any;
        };
      };
      socialLinks: {
        url: string;
        label: string;
        icon: Array<string>;
      }[];
      siteName: string;
    };
  };
};

const Navbar: React.FC = () => {
  const draftboxSettingsData: DraftboxSettings = useStaticQuery(graphql`
    query {
      draftboxSettings {
        content {
          header {
            navigation {
              label
              url
            }
            logoType
            logoText
            logoLink
            openLogoLinkInNewTab
          }
        }
        identity {
          logoUrl {
            publicURL
            childImageSharp {
              fixed(height: 30) {
                ...GatsbyImageSharpFixed_withWebp
              }
            }
          }
          socialLinks {
            url
            label
            icon
          }
          siteName
        }
      }
    }
  `);

  const {
    draftboxSettings: {
      content: { header: draftboxHeader },
      identity: { logoUrl: draftboxLogoUrl, siteName, socialLinks },
    },
  } = draftboxSettingsData;

  const [isMenuToggled, setIsMenuToggled] = useState(false);

  const Logo = (
    <>
      {draftboxLogoUrl && (
        <>
          {draftboxLogoUrl?.childImageSharp?.fixed && (
            <Img
              loading="eager"
              fadeIn={false}
              fixed={draftboxLogoUrl?.childImageSharp?.fixed}
            />
          )}

          {!draftboxLogoUrl?.childImageSharp?.fixed && (
            <img
              height={24}
              width={90}
              className="h-6 w-auto"
              src={draftboxLogoUrl.publicURL}
              alt={siteName}
            />
          )}
        </>
      )}
      {!draftboxLogoUrl && (
        <>
          {draftboxHeader.logoType === "text" && (
            <span
              className="dangerous-html text-xl"
              dangerouslySetInnerHTML={{
                __html: draftboxHeader.logoText,
              }}
            ></span>
          )}
          {draftboxHeader.logoType === "firstLetter" && (
            <span
              style={{ height: 30, width: 30 }}
              className="dangerous-html text-lg bg-primary inline-flex justify-center items-center rounded text-white"
              dangerouslySetInnerHTML={{ __html: siteName[0] }}
            ></span>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <nav className="flex flex-wrap items-center justify-between px-6 py-4 container mx-auto">
        <div className="flex items-center flex-shrink-0 mr-4 w-max lg:w-auto">
          {draftboxHeader.logoLink.startsWith("/") &&
          !draftboxHeader.openLogoLinkInNewTab ? (
            <Link
              aria-label="home"
              className="text-xl text-primary font-sansBold flex items-center"
              to={draftboxHeader.logoLink}
            >
              {Logo}
            </Link>
          ) : (
            <a
              className="text-xl text-primary font-sansBold flex items-center"
              href={draftboxHeader.logoLink}
              target={draftboxHeader.openLogoLinkInNewTab ? "_blank" : "_self"}
            >
              {Logo}
            </a>
          )}
        </div>
        <div className="block lg:hidden">
          {" "}
          {draftboxHeader.navigation.length > 0 && (
            <button
              aria-label="menu-toggle"
              onClick={(e) => setIsMenuToggled(!isMenuToggled)}
              className="navbar-burger flex items-center py-2 text-gray-700 focus:outline-none"
            >
              {!isMenuToggled ? <i className="fas fa-bars fa-lg"></i> : <></>}
            </button>
          )}{" "}
        </div>
        <div
          className={classNames(
            "navbar-menu hidden lg:flex lg:flex-grow lg:items-center w-full lg:w-auto transform transition-transform duration-100"
          )}
        >
          <div className="flex items-center flex-col lg:flex-row">
            {draftboxHeader.navigation.map(({ label, url }, index) => {
              return url.startsWith("/") ? (
                <Link
                  key={index}
                  className="inline-block text-xs uppercase font-sansMedium mt-4 lg:mt-0 mx-3 lg:mx-3 text-gray-700 navbar-link py-1"
                  activeClassName="navbar-link-active"
                  to={url}
                >
                  {label}
                </Link>
              ) : (
                <a
                  key={index}
                  href={url}
                  rel="noreferrer noopener"
                  target="_blank"
                  className="inline-block text-xs uppercase font-sansMedium mt-4 lg:mt-0 mx-3 lg:mx-3 text-gray-700 navbar-link py-1"
                >
                  {label}
                </a>
              );
            })}
          </div>
        </div>
        <div className="navbar-menu hidden lg:flex lg:flex-grow lg:items-center lg:justify-end w-full lg:w-1/5 my-2 lg:my-0 mt-4 lg:mt-0 space-x-4">
          {socialLinks.map((social, index) => {
            return social.url.startsWith("/") ? (
              <Link key={index} to={social.url}>
                <i
                  className={`${social.icon[0]} fa-${social.icon[1]} text-gray-700 hover:text-primary`}
                ></i>
              </Link>
            ) : (
              <a
                key={index}
                rel="noreferrer noopener"
                href={`${social.url}`}
                target="_blank"
                aria-label={social.label}
              >
                <i
                  className={`${social.icon[0]} fa-${social.icon[1]} text-gray-700 hover:text-primary`}
                ></i>
              </a>
            );
          })}
        </div>
      </nav>
      <div
        className={classNames(
          "navbar-menu bg-white  fixed right-0 top-0 w-full h-full overflow-y-auto lg:w-auto transform transition duration-300 py-4 px-6 flex lg:hidden flex-col",
          {
            " opacity-100 z-50": isMenuToggled,
            " opacity-0 -z-1": !isMenuToggled,
          }
        )}
      >
        <div className="flex justify-between items-center">
          {draftboxHeader.logoLink.startsWith("/") &&
          !draftboxHeader.openLogoLinkInNewTab ? (
            <Link
              aria-label="home"
              className="text-2xl text-primary font-sansBold flex items-center w-max mr-4"
              to="/"
            >
              {Logo}
            </Link>
          ) : (
            <a
              aria-label="home"
              className="text-2xl text-primary font-sansBold flex items-center w-max mr-4"
              href="/"
              target={draftboxHeader.openLogoLinkInNewTab ? "_blank" : "_self"}
            >
              {Logo}
            </a>
          )}
          <button
            aria-label="menu-toggle"
            onClick={(e) => setIsMenuToggled(!isMenuToggled)}
            className="navbar-burger z-50 flex items-center py-2 text-gray-700 focus:outline-none"
          >
            {!isMenuToggled ? (
              <i className="fas fa-bars fa-lg"></i>
            ) : (
              <i className="fas fa-times fa-lg"></i>
            )}
          </button>
        </div>
        <hr className="my-4" />
        <div className="flex flex-col space-y-4">
          {draftboxHeader.navigation.map(({ label, url }, index) => {
            return url.startsWith("/") ? (
              <span key={index}>
                <Link
                  key={index}
                  className="inline-block lg:mt-0 lg:mx-5 text-gray-700 border-b-3 border-transparent hover:border-primary hover:border-b-3"
                  activeClassName="border-b-3 border-primaryLight"
                  to={url}
                >
                  {label}
                </Link>
              </span>
            ) : (
              <span key={index}>
                <a
                  key={index}
                  href={url}
                  rel="noreferrer noopener"
                  target="_blank"
                  className="inline-block lg:mt-0 lg:mx-5 text-gray-700 border-b-3 border-transparent hover:border-primary hover:border-b-3"
                >
                  {label}
                </a>
              </span>
            );
          })}
        </div>
        {socialLinks?.length > 0 && (
          <>
            <hr className="my-4" />
            <div className="flex flex-wrap items-center w-full lg:w-1/5 my-2 lg:my-0 space-x-6">
              {socialLinks.map((social, index) => {
                return (
                  <a
                    key={index}
                    rel="noreferrer noopener"
                    href={`${social.url}`}
                    target="_blank"
                    aria-label={social.label}
                  >
                    <i
                      className={`${social.icon[0]} fa-${social.icon[1]} fa-lg text-gray-700 hover:text-primary`}
                    ></i>
                  </a>
                );
              })}
            </div>
          </>
        )}
        <div className="pb-4 w-full"></div>
      </div>
    </>
  );
};

export default Navbar;
